import type { Document, DocumentSourceProvenance } from '@agentwiki/shared'

type DocumentLike =
  | Pick<Document, 'sourceProvenance'>
  | { sourceProvenance?: DocumentSourceProvenance | null }

export type DocumentKind = 'verified' | 'stale' | 'conflict' | 'draft'

export interface DocumentKindInfo {
  kind: DocumentKind
  label: string
  shortLabel: string
  description: string
}

export interface DocumentOwnershipInfo {
  ownership: 'manual' | 'source-backed'
  label: 'Manual document' | 'Source-backed'
  shortLabel: 'Manual' | 'Source-backed'
  description: string
}

export function getDocumentOwnershipInfo(doc?: DocumentLike | null): DocumentOwnershipInfo {
  if (!doc?.sourceProvenance) {
    return {
      ownership: 'manual',
      label: 'Manual document',
      shortLabel: 'Manual',
      description: 'Managed directly in AgentWiki',
    }
  }
  return {
    ownership: 'source-backed',
    label: 'Source-backed',
    shortLabel: 'Source-backed',
    description: 'Owned by a synced external source',
  }
}

export type DocumentVisibility = Document['accessLevel']

export interface DocumentVisibilityInfo {
  visibility: DocumentVisibility
  label: string
  shortLabel: string
}

export function getDocumentKindInfo(doc?: DocumentLike | null): DocumentKindInfo {
  const source = doc?.sourceProvenance
  if (!source) {
    return {
      kind: 'draft',
      label: 'Manual document',
      shortLabel: 'Manual',
      description: 'Managed directly in AgentWiki',
    }
  }

  if (source.hasSyncConflict || source.syncStatus === 'conflict') {
    return {
      kind: 'conflict',
      label: 'Sync conflict',
      shortLabel: 'Conflict',
      description: 'Source snapshot conflict requires a separate resolution',
    }
  }

  if (source.isStale || source.syncStatus !== 'synced') {
    return {
      kind: 'stale',
      label: 'Needs sync',
      shortLabel: 'Stale',
      description: 'Compiled knowledge is behind its source',
    }
  }

  return {
    kind: 'verified',
    label: 'Source-backed',
    shortLabel: 'Source',
    description: 'Compiled from synced source provenance',
  }
}

export function getDocumentVisibilityInfo(
  doc?: Pick<Document, 'accessLevel'> | null,
): DocumentVisibilityInfo {
  const visibility = doc?.accessLevel ?? 'private'

  if (visibility === 'public') {
    return { visibility, label: 'Public access', shortLabel: 'Public' }
  }

  if (visibility === 'specific') {
    return { visibility, label: 'Specific access', shortLabel: 'Specific' }
  }

  return { visibility, label: 'Private access', shortLabel: 'Private' }
}

export function isSourceBackedDocument(
  doc?: { sourceProvenance?: unknown | null } | null,
): boolean {
  return Boolean(doc?.sourceProvenance)
}

export function isVerifiedKnowledge(doc?: DocumentLike | null): boolean {
  return getDocumentKindInfo(doc).kind === 'verified'
}

export function formatCompileMode(mode?: string | null) {
  if (!mode) return 'Manual'
  return mode.charAt(0).toUpperCase() + mode.slice(1)
}

export function formatMemoryClass(memoryClass?: string | null) {
  if (!memoryClass) return 'Unclassified'
  return memoryClass
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

export function formatSourceType(sourceType?: string | null) {
  if (!sourceType) return 'Manual'
  return sourceType
    .split(/[-_]/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

export function formatSourcePath(path?: string | null, maxLength = 56) {
  if (!path) return 'No source path'
  if (path.length <= maxLength) return path
  return `...${path.slice(-(maxLength - 3))}`
}

export function formatRelativeSyncTime(value?: number | Date | string | null) {
  if (!value) return 'Not synced'
  const timestamp = new Date(value).getTime()
  if (!Number.isFinite(timestamp)) return 'Not synced'

  const deltaMs = Date.now() - timestamp
  if (deltaMs < 60_000) return 'just now'
  const minutes = Math.floor(deltaMs / 60_000)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  return new Date(timestamp).toLocaleDateString()
}
